import React, { useEffect, useState, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, ArrowRight, Share2, Twitter, Linkedin, Facebook, Link2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePageMeta } from '../hooks/usePageMeta';
import { useBlogPosts } from '../hooks/useBlogPosts';
import { trackBlogRead } from '../hooks/useAnalytics';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { CTASection } from '../components/sections/CTASection';
import { useQuoteModal } from '../hooks/useQuoteModal';
const categoryColors: Record<string, string> = {
  Strategy: 'bg-[#0D9488] text-white',
  Design: 'bg-[#FF6600] text-white',
  Development: 'bg-[#00316B] text-white',
  Branding: 'bg-[#7C3AED] text-white'
};
function ShareButtons({
  title
}: {
  title: string;
}) {
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const share = (platform: string) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const urls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
    };
    if (urls[platform]) {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert('Link copied!');
    } catch {
      // fallback
    }
  };
  return <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-607">
      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3" data-id="element-608">
        Share this article
      </p>
      <div className="flex items-center gap-3" data-id="element-609">
        <button onClick={() => share('twitter')} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-[#1DA1F2] hover:text-white flex items-center justify-center text-gray-600 transition-colors" aria-label="Share on Twitter" data-id="element-610">
          <Twitter className="w-4 h-4" data-id="element-611" />
        </button>
        <button onClick={() => share('linkedin')} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-[#0A66C2] hover:text-white flex items-center justify-center text-gray-600 transition-colors" aria-label="Share on LinkedIn" data-id="element-612">
          <Linkedin className="w-4 h-4" data-id="element-613" />
        </button>
        <button onClick={() => share('facebook')} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-[#1877F2] hover:text-white flex items-center justify-center text-gray-600 transition-colors" aria-label="Share on Facebook" data-id="element-614">
          <Facebook className="w-4 h-4" data-id="element-615" />
        </button>
        <button onClick={copyLink} className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-800 hover:text-white flex items-center justify-center text-gray-600 transition-colors" aria-label="Copy link" data-id="element-616">
          <Link2 className="w-4 h-4" data-id="element-617" />
        </button>
      </div>
    </div>;
}
function renderBody(body: string) {
  return body.split('\n\n').map((block, i) => {
    if (block.startsWith('## ')) {
      return <h2 key={i} className="text-2xl font-bold text-[#0C0C0C] mt-10 mb-4 font-heading" data-id="element-618">
          {block.replace('## ', '')}
        </h2>;
    }
    if (block.startsWith('**') && block.endsWith('**')) {
      return <p key={i} className="font-semibold text-[#0C0C0C] mb-4" data-id="element-619">
          {block.replace(/\*\*/g, '')}
        </p>;
    }
    // Handle inline bold
    const parts = block.split(/(\*\*[^*]+\*\*)/);
    return <p key={i} className="text-gray-700 leading-relaxed mb-6 text-lg" data-id="element-620">
        {parts.map((part, j) => part.startsWith('**') && part.endsWith('**') ? <strong key={j} className="font-semibold text-[#0C0C0C]" data-id="element-621">
              {part.replace(/\*\*/g, '')}
            </strong> : part)}
      </p>;
  });
}
export function BlogPostPage() {
  const {
    openModal
  } = useQuoteModal();
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    posts
  } = useBlogPosts();
  const post = posts.find(p => p.slug === slug);
  usePageMeta({
    title: post?.title || 'Blog Post',
    description: post?.excerpt || 'Read this article on the Belo Digital blog.',
    image: post?.image,
    type: 'article'
  });
  // Track blog post read
  useEffect(() => {
    if (slug) {
      trackBlogRead(slug);
    }
  }, [slug]);
  if (!post) {
    return <main className="min-h-screen bg-[#F8F8F6] pt-32 flex flex-col items-center justify-center text-center px-4" data-id="element-622">
        <h1 className="text-4xl font-bold text-[#0C0C0C] mb-4" data-id="element-623">
          Post Not Found
        </h1>
        <p className="text-gray-600 mb-8" data-id="element-624">
          This article doesn't exist or may have been moved.
        </p>
        <Button onClick={() => navigate('/blog')} data-id="element-625">Back to Blog</Button>
      </main>;
  }
  const related = posts.filter(p => p.id !== post.id && p.category === post.category).slice(0, 2);
  const fallbackRelated = posts.filter(p => p.id !== post.id).slice(0, 2);
  const relatedPosts = related.length >= 2 ? related : fallbackRelated;
  return <main className="bg-[#F8F8F6] min-h-screen" data-id="element-626">
      {/* Full-bleed Hero Banner */}
      <div className="relative w-full h-[340px] md:h-[420px] lg:h-[480px] bg-[#00316B]" data-id="element-627">
        {post.image && <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover opacity-60" data-id="element-628" />}
        {/* Blue overlay */}
        <div className="absolute inset-0 bg-[#00316B]/50" data-id="element-629" />

        {/* Content on top of image */}
        <div className="relative z-10 h-full flex flex-col justify-end max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14" data-id="element-630">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-631">
            <Link to="/blog" className="inline-flex items-center text-sm font-medium text-white/70 hover:text-white transition-colors mb-6 w-fit group" data-id="element-632">
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-633" />
              Back to Blog
            </Link>

            <div className="flex items-center gap-3 mb-4" data-id="element-634">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${categoryColors[post.category] ?? 'bg-gray-200 text-gray-800'}`} data-id="element-635">
                {post.category}
              </span>
              <span className="text-blue-200 text-sm flex items-center gap-1" data-id="element-636">
                <Clock className="w-3 h-3" data-id="element-637" /> {post.readTime}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-heading max-w-4xl" data-id="element-638">
              {post.title}
            </h1>

            <div className="flex items-center gap-6 text-blue-200 text-sm" data-id="element-639">
              <span className="flex items-center gap-1.5" data-id="element-640">
                <User className="w-4 h-4" data-id="element-641" /> {post.author}
              </span>
              <span className="flex items-center gap-1.5" data-id="element-642">
                <Calendar className="w-4 h-4" data-id="element-643" /> {post.date}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Article Body */}
      <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-644">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-16 items-start" data-id="element-645">
          {/* Content */}
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-646">
            <p className="text-xl text-gray-600 leading-relaxed mb-8 font-medium border-l-4 border-[#FF6600] pl-6" data-id="element-647">
              {post.excerpt}
            </p>
            <div className="prose-content max-w-4xl" data-id="element-648">
              {renderBody(post.body)}
            </div>

            {/* Share / Tags */}
            <div className="mt-12 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 max-w-4xl" data-id="element-649">
              <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${categoryColors[post.category] ?? 'bg-gray-200 text-gray-800'}`} data-id="element-650">
                {post.category}
              </span>
              <Link to="/blog" data-id="element-651">
                <Button variant="outline" size="sm" className="group" data-id="element-652">
                  More Articles
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-653" />
                </Button>
              </Link>
            </div>

            <div className="max-w-4xl" data-id="element-654">
              <ShareButtons title={post.title} data-id="element-655" />
            </div>
          </motion.article>

          {/* Sidebar */}
          <aside className="space-y-8 lg:sticky lg:top-28" data-id="element-656">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-657">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4" data-id="element-658">
                About the Author
              </h3>
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100 shadow-sm" data-id="element-659">
                <div className="w-12 h-12 rounded-full bg-[#00316B] flex items-center justify-center text-white font-bold text-lg flex-shrink-0" data-id="element-660">
                  {post.author.charAt(0)}
                </div>
                <div data-id="element-661">
                  <p className="font-semibold text-[#0C0C0C]" data-id="element-662">{post.author}</p>
                  <p className="text-sm text-gray-500" data-id="element-663">Belo Digital Team</p>
                </div>
              </div>
            </motion.div>

            {relatedPosts.length > 0 && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-664">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4" data-id="element-665">
                  Related Articles
                </h3>
                <div className="space-y-4" data-id="element-666">
                  {relatedPosts.map(related => <Link key={related.id} to={`/blog/${related.slug}`} className="block" data-id="element-667">
                      <Card className="p-4 group cursor-pointer hover:border-blue-100 transition-colors" hoverEffect data-id="element-668">
                        <div className="flex gap-3" data-id="element-669">
                          <img src={related.image} alt={related.title} className="w-16 h-16 object-cover rounded-lg flex-shrink-0" data-id="element-670" />
                          <div data-id="element-671">
                            <p className="text-xs text-[#FF6600] font-semibold mb-1" data-id="element-672">
                              {related.category}
                            </p>
                            <p className="text-sm font-semibold text-[#0C0C0C] group-hover:text-[#00316B] transition-colors line-clamp-2 leading-snug" data-id="element-673">
                              {related.title}
                            </p>
                          </div>
                        </div>
                      </Card>
                    </Link>)}
                </div>
              </motion.div>}

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.6
          }} className="bg-[#00316B] rounded-2xl p-6 text-white" data-id="element-674">
              <h3 className="font-bold text-lg mb-2" data-id="element-675">
                Have a project in mind?
              </h3>
              <p className="text-blue-200 text-sm mb-4" data-id="element-676">
                Let's build something great together.
              </p>
              <Button size="sm" className="bg-[#FF6600] hover:bg-[#CC5200] text-white w-full" onClick={openModal} data-id="element-677">
                Request a Quote
              </Button>
            </motion.div>
          </aside>
        </div>
      </div>

      <CTASection data-id="element-678" />
    </main>;
}